/**
 * Story 564: project settings as a page in the workspace.
 *
 * settings.page.sections: a list of sections on the left, from one registry
 * (settingsSections.ts), with the shown one marked; on narrow screens a
 * chooser replaces the list. Moving to a section puts focus on its heading.
 * settings.page.read_only: anyone but the owner sees one note at the top and
 * every section read-only. settings.page.route: the workspace chooses this page
 * from its address (pages/Workspace.tsx); a failed read says so and Back still
 * works.
 *
 * Replaces the Project settings dialog (stories 514, 540, 556). Strictness and
 * the content validation service stay stored and are not rendered here, as in
 * the dialog (operator, 2026-09-28).
 */

import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { useProjectSettings } from '../../hooks/useProjectSettings';
import { SETTINGS_SECTIONS } from './settingsSections';
import { resolveSettingsSection, withSettingsOpen } from './settingsUrl';
import {
  COMMIT_LOAD_FAILED_MESSAGE,
  SETTINGS_TITLE,
  SETTINGS_BACK_LABEL,
  SETTINGS_OWNER_ONLY_NOTE,
  SETTINGS_SECTION_CHOOSER_LABEL,
  SETTINGS_LOADING_MESSAGE,
} from '../workspace/projectSettingsCopy';
import './ProjectSettingsView.css';

const OWNER_ROLE = 'owner';
const CHOOSER_ID = 'project-settings-section-chooser';
const HEADING_ID = 'project-settings-section-heading';

interface ProjectSettingsViewProps {
  projectSlug: string;
  /** The settings parameter from the address; resolved to a registered section. */
  sectionParam: string | null;
  onSelectSection: (sectionId: string) => void;
  onBack: () => void;
}

/** The address of a section, so a section link can be opened in a new tab. */
function sectionHref(sectionId: string): string {
  return `?${withSettingsOpen(new URLSearchParams(window.location.search), sectionId).toString()}`;
}

export function ProjectSettingsView({ projectSlug, sectionParam, onSelectSection, onBack }: ProjectSettingsViewProps) {
  const { data, isLoading, error } = useProjectSettings(projectSlug);
  const sectionId = resolveSettingsSection(sectionParam, SETTINGS_SECTIONS);
  const section = SETTINGS_SECTIONS.find((s) => s.id === sectionId)!;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [refusedForRole, setRefusedForRole] = useState(false);

  const readOnly = (data !== undefined && data.role !== OWNER_ROLE) || refusedForRole;

  // Moving to a section, including arriving on the page, puts focus on its heading.
  useEffect(() => {
    headingRef.current?.focus();
  }, [sectionId]);

  const handleLinkClick = useCallback((event: MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    onSelectSection(id);
  }, [onSelectSection]);

  const handleRefusedForRole = useCallback(() => setRefusedForRole(true), []);

  const SectionComponent = section.Component;

  return (
    <main className="project-settings">
      <div className="project-settings__top">
        <button type="button" className="project-settings__back" onClick={onBack}>
          <span aria-hidden="true">← </span>{SETTINGS_BACK_LABEL}
        </button>
        <span className="project-settings__project">{projectSlug}</span>
      </div>

      <div className="project-settings__body">
        <div className="project-settings__sidebar">
          <h1 className="project-settings__title">{SETTINGS_TITLE}</h1>
          <nav className="project-settings__nav" aria-label={SETTINGS_TITLE}>
            <ul>
              {SETTINGS_SECTIONS.map((s) => (
                <li key={s.id}>
                  <a
                    href={sectionHref(s.id)}
                    className="project-settings__nav-link"
                    aria-current={s.id === sectionId ? 'page' : undefined}
                    onClick={(event) => handleLinkClick(event, s.id)}
                  >
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
          <div className="project-settings__chooser">
            <label htmlFor={CHOOSER_ID}>{SETTINGS_SECTION_CHOOSER_LABEL}</label>
            <select id={CHOOSER_ID} value={sectionId} onChange={(event) => onSelectSection(event.target.value)}>
              {SETTINGS_SECTIONS.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        <section className="project-settings__content" aria-labelledby={HEADING_ID}>
          {readOnly && <p className="project-settings__note">{SETTINGS_OWNER_ONLY_NOTE}</p>}
          <h2 id={HEADING_ID} ref={headingRef} tabIndex={-1} className="project-settings__heading">{section.label}</h2>

          {isLoading && <p className="project-settings__message">{SETTINGS_LOADING_MESSAGE}</p>}

          {error && !data && (
            <p className="project-settings__error" role="alert">{COMMIT_LOAD_FAILED_MESSAGE}</p>
          )}

          {data && (
            <SectionComponent
              key={`${projectSlug}:${sectionId}`}
              projectSlug={projectSlug}
              settings={data}
              readOnly={readOnly}
              onRefusedForRole={handleRefusedForRole}
            />
          )}
        </section>
      </div>
    </main>
  );
}
